import { useState } from "react";
import { motion } from "framer-motion";
import { PageHero } from "../components/site/PageHero";
import { FadeUp } from "../components/site/Motion";
import { ROUTES, DESTINOS } from "../lib/site";
import { Link } from "react-router-dom";
import { routePagesList, routePath } from "../lib/routePages";

const Timeline = ({ route }) => (
  <motion.ol key={route.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} data-testid={`route-timeline-${route.id}`} className="relative ml-2 border-l-2 border-dashed border-[var(--forest)]/30">
    {route.stops.map(([s, t], i) => {
      const key = i === 0 || i === route.stops.length - 1 || /^(Retiro|Rosario|Santa Fe|Santiago del Estero|La Banda|Tucumán|Termas)/.test(s);
      return (
        <motion.li key={`${s}-${i}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.025 }} className="relative flex items-baseline justify-between gap-6 py-3 pl-8">
          <span className={`absolute -left-[9px] top-[18px] h-4 w-4 rounded-full border-2 border-[var(--forest)] ${key ? "bg-[var(--lime)]" : "bg-[var(--paper)]"}`} />
          <span className={key ? "font-display-up text-lg" : "text-[var(--ink)]"}>{s}</span>
          <span className="font-mono text-sm text-[var(--forest)]">{t}</span>
        </motion.li>
      );
    })}
  </motion.ol>
);

export default function Destinos() {
  const [active, setActive] = useState(ROUTES[0].id);
  const route = ROUTES.find((r) => r.id === active);

  return (
    <>
      <PageHero testid="destinos-hero" eyebrow="Recorridos y horarios" lines={["Llegamos donde", "vos querés estar."]} intro="Elegí tu recorrido y mirá cada parada con su horario de referencia." />
      <section className="bg-[var(--paper)] pb-24">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex flex-col gap-2 lg:sticky lg:top-28">
              {ROUTES.map((r) => (
                <button key={r.id} onClick={() => setActive(r.id)} data-testid={`route-tab-${r.id}`} className={`rounded-2xl px-6 py-5 text-left font-semibold transition-colors duration-300 ${active === r.id ? "bg-[var(--forest)] text-white" : "bg-white hover:bg-[#e9e8df]"}`}>
                  <span className={`block font-mono text-[11px] uppercase tracking-[0.2em] ${active === r.id ? "text-[var(--lime)]" : "text-[var(--muted-ink)]"}`}>{r.days} · {r.stops.length} paradas</span>
                  <span className="mt-1 block text-lg">{r.name}</span>
                </button>
              ))}
              <p className="mt-3 text-xs text-[var(--muted-ink)]">* Horarios estimados: en cada parada es el horario de salida y en el destino, el de llegada. Pueden cambiar sin previo aviso.</p>
            </div>
          </div>
          <div className="rounded-[28px] bg-white p-6 md:p-10 lg:col-span-8">
            <p className="mb-6 font-display text-3xl md:text-4xl">{route.name}</p>
            <p data-testid="route-active-days" className="-mt-4 mb-6 font-semibold text-[var(--forest)]">Sale: {route.days}</p>
            <Timeline route={route} />
          </div>
        </div>
      </section>
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-4 px-5 sm:grid-cols-2 md:px-10 lg:grid-cols-5">
          {DESTINOS.map((d, i) => (
            <FadeUp key={d.city} delay={i * 0.06}>
              <div data-testid={`destino-card-${i}`} className="group relative aspect-[3/4] overflow-hidden rounded-[24px]">
                <img src={d.img} alt={d.city} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--lime)]">{d.tag}</p>
                  <p className="mt-1 font-display text-2xl text-white">{d.city}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
      <section data-testid="route-links" className="bg-[var(--paper)] py-20">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <h2 className="font-display text-base md:text-lg">Pasajes por tramo</h2>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            {Object.entries(routePagesList().reduce((acc, p) => ({ ...acc, [p.origin]: [...(acc[p.origin] || []), p] }), {})).map(([origin, list]) => (
              <details key={origin} data-testid={`route-group-${origin}`} className="rounded-2xl bg-white px-5 py-3">
                <summary className="cursor-pointer text-sm font-bold">Desde {origin} <span className="font-normal text-[var(--muted-ink)]">· {list.length} destinos</span></summary>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {list.map((p) => <li key={p.slug}><Link to={routePath(p)} data-testid={`route-link-${p.slug}`} className="inline-block rounded-full bg-[var(--paper)] px-3 py-1.5 text-sm transition-colors hover:bg-[var(--lime)]">{p.destination}</Link></li>)}
                </ul>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
